import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Archive, ArchiveRestore, ChevronLeft, ChevronRight, FileSpreadsheet, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog } from "@/components/ui/dialog";
import {
  AppDialogBody,
  AppDialogContent,
  AppDialogFooter,
  appDialogPrimaryButton,
  appDialogSecondaryButton,
} from "@/components/ui/app-dialog";
import {
  archiveGameBankSheet,
  getGameBankSheets,
  GameBankLottery,
  GameBankSheetStatus,
  GameBankSheetSummary,
  GameBankSheetsPaginated,
  unarchiveGameBankSheet,
} from "@/Api/gameBank";
import { cn } from "@/lib/utils";
import { CheckStatusBadge, formatDateTime, pad2, SheetStatusBadge } from "./shared";

const PER_PAGE = 10;

/** "active" = em andamento e finalizadas (a listagem padrão da API, sem as arquivadas) */
type SheetFilter = "active" | GameBankSheetStatus;

const FILTERS: { value: SheetFilter; label: string }[] = [
  { value: "active", label: "Ativas" },
  { value: "in_progress", label: "Em andamento" },
  { value: "finished", label: "Finalizadas" },
  { value: "archived", label: "Arquivadas" },
];

/** Detalhe embaixo dos status: contadores da conferência, motivo de ter voltado para Pendente ou data da injeção */
const StatusDetail = ({ sheet }: { sheet: GameBankSheetSummary }) => {
  if (sheet.saved_at) {
    return <span className="text-xs text-muted-foreground">Jogos no banco desde {formatDateTime(sheet.saved_at)}</span>;
  }

  if (sheet.status === "archived") {
    return <span className="text-xs text-muted-foreground">Descontinuada: os jogos não foram para o banco</span>;
  }

  if (sheet.check_status === "analyzed") {
    const { ok, attention, repeated, invalid } = sheet.summary;
    return (
      <span className={cn("text-xs", sheet.can_finish ? "text-muted-foreground" : "text-red-600")}>
        {ok} ok · {attention} atenção · {repeated} {repeated === 1 ? "repetido" : "repetidos"}
        {invalid > 0 && ` · ${invalid} ${invalid === 1 ? "inválido" : "inválidos"}`}
      </span>
    );
  }

  if (sheet.invalidated_at && sheet.invalidated_by_sheet_number) {
    return (
      <span className="text-xs text-amber-700 dark:text-amber-300">
        Conferir de novo: a Planilha {sheet.invalidated_by_sheet_number} foi finalizada
      </span>
    );
  }

  return null;
};

/**
 * Banco de Jogos > aba da loteria > Planilhas (LOT-62).
 * Em andamento podem ser continuadas por quem tem a permissão de cadastro; finalizadas são só leitura.
 * Arquivar tira a planilha da listagem (descontinua uma em andamento ou esconde uma finalizada).
 */
export const GameBankSheetsPanel = ({
  lottery,
  canManage,
  onChanged,
}: {
  lottery: GameBankLottery;
  canManage: boolean;
  /** Avisa a página para atualizar os contadores das abas */
  onChanged: () => void;
}) => {
  const [filter, setFilter] = useState<SheetFilter>("active");
  const [page, setPage] = useState(1);
  const [data, setData] = useState<GameBankSheetsPaginated | null>(null);
  const [loading, setLoading] = useState(true);
  const [toArchive, setToArchive] = useState<GameBankSheetSummary | null>(null);
  const [busyUuid, setBusyUuid] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { ok, data: body } = await getGameBankSheets({
        lottery_id: lottery.id,
        status: filter === "active" ? undefined : filter,
        page,
        per_page: PER_PAGE,
      });
      if (ok && body?.response) setData(body.response);
    } finally {
      setLoading(false);
    }
  }, [lottery.id, filter, page]);

  useEffect(() => {
    load();
  }, [load]);

  const changeFilter = (value: SheetFilter) => {
    setFilter(value);
    setPage(1);
  };

  const confirmArchive = async () => {
    if (!toArchive) return;
    setBusyUuid(toArchive.uuid);
    try {
      const { ok } = await archiveGameBankSheet(toArchive.uuid);
      if (ok) {
        toast.success(`Planilha ${toArchive.number} arquivada.`);
        setToArchive(null);
        load();
        onChanged();
      }
    } finally {
      setBusyUuid(null);
    }
  };

  const unarchive = async (sheet: GameBankSheetSummary) => {
    setBusyUuid(sheet.uuid);
    try {
      const { ok } = await unarchiveGameBankSheet(sheet.uuid);
      if (ok) {
        toast.success(
          sheet.saved_at
            ? `Planilha ${sheet.number} desarquivada (finalizada).`
            : `Planilha ${sheet.number} desarquivada: confira os jogos de novo antes de finalizar.`
        );
        load();
        onChanged();
      }
    } finally {
      setBusyUuid(null);
    }
  };

  const sheets = data?.data ?? [];
  const filterLabel = FILTERS.find((item) => item.value === filter)?.label.toLowerCase();

  return (
    <Card>
      <CardContent className="space-y-4 p-4 sm:p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="flex items-center gap-2 text-base font-semibold">
              <FileSpreadsheet className="h-4 w-4 text-muted-foreground" />
              Planilhas
            </h2>
            <p className="text-sm text-muted-foreground">
              Os jogos só entram no banco quando a planilha é conferida e finalizada.
            </p>
          </div>
          <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filtrar planilhas por status">
            {FILTERS.map((item) => (
              <button
                key={item.value}
                type="button"
                onClick={() => changeFilter(item.value)}
                aria-pressed={filter === item.value}
                className={cn(
                  "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                  filter === item.value
                    ? "border-blue-600 bg-blue-600 text-white"
                    : "text-muted-foreground hover:bg-muted"
                )}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        {loading && !data ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : sheets.length === 0 ? (
          <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
            {filter === "active"
              ? `Nenhuma planilha em ${lottery.name}. Use "Cadastrar jogos" para começar uma.`
              : `Nenhuma planilha ${filterLabel} em ${lottery.name}.`}
          </div>
        ) : (
          <div className={cn("overflow-hidden rounded-lg border", loading && "opacity-60")}>
            <div className="hidden grid-cols-[1.1fr_0.8fr_0.6fr_1.8fr_1.1fr_auto] gap-4 bg-muted/60 px-4 py-3 text-sm font-semibold lg:grid">
              <span>Planilha</span>
              <span>Dezenas</span>
              <span>Jogos</span>
              <span>Status</span>
              <span>Última alteração</span>
              <span className="w-36 text-right">Ações</span>
            </div>
            {sheets.map((sheet) => {
              const editable = canManage && sheet.status === "in_progress";
              const busy = busyUuid === sheet.uuid;
              return (
                <div
                  key={sheet.uuid}
                  className="flex flex-col gap-2 border-t px-4 py-3 lg:grid lg:grid-cols-[1.1fr_0.8fr_0.6fr_1.8fr_1.1fr_auto] lg:items-center lg:gap-4"
                >
                  <div className="min-w-0">
                    <Link
                      to={`/banco-de-boloes/${lottery.id}/planilhas/${sheet.uuid}`}
                      className="font-medium text-blue-600 hover:text-blue-800"
                    >
                      Planilha {sheet.number}
                    </Link>
                    <p className="truncate text-xs text-muted-foreground">
                      Criada por {sheet.created_by?.name ?? "-"}
                    </p>
                  </div>
                  <span className="text-sm">{pad2(sheet.dezenas)} dezenas</span>
                  <span className="text-sm tabular-nums">
                    {sheet.rows_count} {sheet.rows_count === 1 ? "jogo" : "jogos"}
                  </span>
                  <div className="flex flex-col items-start gap-1">
                    <div className="flex flex-wrap gap-1.5">
                      <SheetStatusBadge status={sheet.status} />
                      {sheet.status === "in_progress" && <CheckStatusBadge status={sheet.check_status} />}
                    </div>
                    <StatusDetail sheet={sheet} />
                  </div>
                  <div className="min-w-0 text-sm">
                    <p>{formatDateTime(sheet.updated_at)}</p>
                    <p className="truncate text-xs text-muted-foreground">{sheet.updated_by?.name ?? ""}</p>
                  </div>
                  <div className="flex w-36 items-center gap-1 lg:justify-end">
                    <Button asChild size="sm" variant="ghost" className="text-blue-600">
                      <Link to={`/banco-de-boloes/${lottery.id}/planilhas/${sheet.uuid}`}>
                        {editable ? "Abrir" : "Ver"}
                      </Link>
                    </Button>
                    {canManage && sheet.status !== "archived" && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-muted-foreground"
                        onClick={() => setToArchive(sheet)}
                        disabled={busy}
                        aria-label={`Arquivar planilha ${sheet.number}`}
                        title="Arquivar"
                      >
                        <Archive className="h-4 w-4" />
                      </Button>
                    )}
                    {canManage && sheet.status === "archived" && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-muted-foreground"
                        onClick={() => unarchive(sheet)}
                        disabled={busy}
                        title="Desarquivar"
                      >
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArchiveRestore className="h-4 w-4" />}
                        <span className="ml-1.5">Desarquivar</span>
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {data && data.last_page > 1 && (
          <div className="flex items-center justify-end gap-2 text-sm">
            <Button
              type="button"
              size="sm"
              className={appDialogSecondaryButton}
              disabled={page <= 1 || loading}
              onClick={() => setPage((current) => current - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-muted-foreground">
              Página {data.current_page} de {data.last_page}
            </span>
            <Button
              type="button"
              size="sm"
              className={appDialogSecondaryButton}
              disabled={page >= data.last_page || loading}
              onClick={() => setPage((current) => current + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </CardContent>

      <Dialog open={!!toArchive} onOpenChange={(open) => !open && setToArchive(null)}>
        <AppDialogContent
          icon={Archive}
          title={toArchive ? `Arquivar Planilha ${toArchive.number}?` : "Arquivar planilha"}
          description="Ela sai da listagem e fica no filtro Arquivadas. Dá para desarquivar depois."
          className="sm:max-w-md"
        >
          <AppDialogBody>
            <p className="text-sm text-muted-foreground">
              {toArchive?.saved_at
                ? `Os ${toArchive.rows_count} jogos dela continuam no banco e no Formador de Bolões.`
                : "A planilha é descontinuada: os jogos dela não entram no banco."}
            </p>
          </AppDialogBody>
          <AppDialogFooter className="flex-col-reverse gap-2 sm:flex-row">
            <Button className={appDialogSecondaryButton} onClick={() => setToArchive(null)} disabled={!!busyUuid}>
              Cancelar
            </Button>
            <Button className={appDialogPrimaryButton} onClick={confirmArchive} disabled={!!busyUuid}>
              {busyUuid && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Arquivar planilha
            </Button>
          </AppDialogFooter>
        </AppDialogContent>
      </Dialog>
    </Card>
  );
};
